import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
  Button,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';

import './MonitoringFilterForm.scss';

function MonitoringFilterForm({
  filter,
  defaultDenounceStatus,
  defaultPossibleFocus,
  defaultYears,
  denounceStatus,
  possibleVectorFocus,
  onSubmit,
  onReset
}) {
  const OnlyRecentOptions = {
    YES: 'Somente os registros deste mês',
    NO: 'Todo o período'
  };
  const INITIAL_YEAR = 2021;

  const possibleDenounceStatus = getPossibleDenounceStatus();
  const possibleVectorFocusMenuItems = getPossibleVectorFocusMenuItems();
  const possibleYears = getPossibleYears();
  const {
    initialOnlyRecent,
    initialDenounceStatus,
    initialPossibleFocus,
    initialYear
  } = getInitialValues({
    possibleDenounceStatus, possibleVectorFocusMenuItems, possibleYears, filter
  });
  const [onlyRecentDenounce, setOnlyRecentDenounce] = useState(initialOnlyRecent ? OnlyRecentOptions.YES : OnlyRecentOptions.NO);

  // Initialization
  function getInitialValues({
    possibleDenounceStatus, possibleVectorFocusMenuItems, possibleYears, filter
  }) {
    if (!possibleDenounceStatus) return;

    const initialOnlyRecent = filter?.focusAge?.[0]?.id === 0;
    const initialDenounceStatus = possibleDenounceStatus?.find(item => item.id === filter?.vectorStatus?.[0]?.id);
    const initialPossibleFocus = possibleVectorFocusMenuItems?.find(item => item.id === filter?.focusTypes?.[0]?.id);
    const initialYear = possibleYears?.find(item => item === filter?.years?.[0]) || defaultYears.ALL
    return {
      initialOnlyRecent,
      initialDenounceStatus: initialDenounceStatus || possibleDenounceStatus[0],
      initialPossibleFocus: initialPossibleFocus || possibleVectorFocusMenuItems[0],
      initialYear
    }
  }

  function getPossibleDenounceStatus() {
    const defaultValue = {
      key: 0,
      id: 0,
      label: defaultDenounceStatus.ALL,
      name: defaultDenounceStatus.ALL
    }
    const options = [defaultValue];
    return options.concat(denounceStatus) || options;
  }

  function getPossibleVectorFocusMenuItems() {
    const defaultValue = {
      key: 0,
      id: 0,
      label: defaultPossibleFocus.ALL,
      name: defaultPossibleFocus.ALL
    }
    const options = [defaultValue];
    return options.concat(possibleVectorFocus) || options;
  }

  function getPossibleYears() {
    const years = [defaultYears.ALL];
    for (let year = INITIAL_YEAR; year <= (new Date()).getFullYear(); year++) {
      years.push(year);
    }
    return years;
  }

  // Actions
  const handleSubmit = (values, { setSubmitting }) => {
    values.onlyRecent = onlyRecentDenounce === OnlyRecentOptions.YES;
    values.denounceStatus = possibleDenounceStatus.find(item => item.label === values.denounceStatusId);
    values.possibleFocus = possibleVectorFocus.find(item => item.label === values.possibleFocusId);
    onSubmit(values, { setSubmitting });
  };

  const handleResetForm = (resetForm) => {
    resetForm();
    setOnlyRecentDenounce(OnlyRecentOptions.NO);
    onReset();
  };

  // Rendering
  if (!initialDenounceStatus?.label || !initialPossibleFocus?.label) {
    return <></>;
  }

  return (
    <Formik
      initialValues={{
        denounceStatusId: initialDenounceStatus.label,
        possibleFocusId: initialPossibleFocus.label,
        onlyRecent: onlyRecentDenounce ? OnlyRecentOptions.YES : OnlyRecentOptions.NO,
        year: initialYear
      }}
      onSubmit={handleSubmit}
    >
      {({ resetForm, isSubmitting }) => (
        <Form className="form monitoring-form">
          <Field
            as={TextField}
            select
            defaultValue={initialDenounceStatus.label}
            className="field select"
            name="denounceStatusId"
            label="Status do foco"
            variant="outlined"
            fullWidth
          >
            {possibleDenounceStatus.map((option) => (
              <MenuItem key={option.key} value={option.label} className="menu-item">
                <div className="menu-item-text-wrapper">
                  <span className="text">{option.label}</span>
                </div>
              </MenuItem>
            ))}
          </Field>
          <ErrorMessage name="denounceStatusId" component="div" />
          <Field
            as={TextField}
            select
            defaultValue={initialPossibleFocus.label}
            className="field select"
            name="possibleFocusId"
            label="Tipo do foco"
            variant="outlined"
            fullWidth
          >
            {possibleVectorFocusMenuItems.map((option) => (
              <MenuItem key={option.key} value={option.label} className="menu-item">
                <div className="menu-item-text-wrapper">
                  <span className="text">{option.label}</span>
                </div>
              </MenuItem>
            ))}
          </Field>
          <ErrorMessage name="possibleFocusId" component="div" />
          <Field
            name="onlyRecent"
            variant="outlined"
            fullWidth
          >
            {({ field }) => (
              <div className="field radio-buttons">
                <FormLabel>Somente os registros deste mês?</FormLabel>
                <RadioGroup>
                  <FormControlLabel
                    value={OnlyRecentOptions.YES}
                    control={
                      <Radio
                        type="radio"
                        checked={onlyRecentDenounce === OnlyRecentOptions.YES}
                        onChange={() => setOnlyRecentDenounce(OnlyRecentOptions.YES)}
                      />}
                    label="Sim"
                    {...field}
                  />
                  <FormControlLabel
                    value={OnlyRecentOptions.NO}
                    control={
                      <Radio
                        type="radio"
                        checked={onlyRecentDenounce === OnlyRecentOptions.NO}
                        onChange={() => setOnlyRecentDenounce(OnlyRecentOptions.NO)}
                      />}
                    label="Não"
                    {...field}
                  />
                </RadioGroup>
              </div>
            )}
          </Field>
          <ErrorMessage name="onlyRecent" component="div" />
          {
            onlyRecentDenounce === OnlyRecentOptions.NO && (
              <div className="years-container">
                <Field
                  as={TextField}
                  select
                  defaultValue={initialPossibleFocus.label}
                  className="field select"
                  name="year"
                  label="Ano"
                  variant="outlined"
                  fullWidth
                >
                  {possibleYears.map((option) => (
                    <MenuItem key={option} value={option} className="menu-item">
                      <div className="menu-item-text-wrapper">
                        <span className="text">{option}</span>
                      </div>
                    </MenuItem>
                  ))}
                </Field>
                <ErrorMessage name="year" component="div" />
              </div>
            )
          }
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="button"
            disabled={isSubmitting}
            fullWidth
          >
            {isSubmitting ? 'Enviando ...' : 'Confirmar o filtro'}
          </Button>
          <Button
            color="primary"
            className="button"
            fullWidth
            onClick={() => handleResetForm(resetForm)}
          >
            Limpar Filtro
          </Button>
        </Form>
      )}
    </Formik>
  );
}

export default MonitoringFilterForm;
